/* eslint-disable react/prop-types */
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { getCampaignArtByUser } from '../../services/campaignService'
import { UserAccountsService } from '../../services'
import {
  getArtGenerationAvailability,
  shouldSidebarRefreshArtLimitsOnPlanEvent,
  snapshotArtAvailability,
  warnIfArtLimitTotalDroppedWithoutUsageIncrease,
} from '../../utils/artConsumption'
import {
  CAMPAIGNS_PAGE_SIZE,
  getCampaignHistoryItemId,
  parseCampaignPageResponse,
  parseKnownNumericValue,
} from '../../utils/campaignHistory'
import {
  LAST_ACTIVE_CAMPAIGN_KEY,
  persistActiveCampaignForReturn,
} from '../../pages/ChatSocketPage/utils/campaignRestore'
import { hasValidAuthToken, isPublicAuthRoute } from '../../utils/unauthorizedSession'
import { pollArtCreditsUntilUpdated } from '../../utils/artCreditsSync'

export const CampaignSidebarContext = createContext(null)

export const CampaignSidebarProvider = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { userData } = useAuth()

  const [isExpanded, setIsExpanded] = useState(true)
  const [campaigns, setCampaigns] = useState([])
  const [selectedCampaignId, setSelectedCampaignId] = useState(null)
  const [campaignsPage, setCampaignsPage] = useState(1)
  const [campaignsSearch, setCampaignsSearch] = useState('')
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false)
  const [hasMoreCampaigns, setHasMoreCampaigns] = useState(false)
  const [campaignHistoryTotalCount, setCampaignHistoryTotalCount] = useState(null)
  const [firstCampaignId, setFirstCampaignId] = useState(null)

  const [availableArts, setAvailableArts] = useState(null)
  const [artesCriadas, setArtesCriadas] = useState(0)
  const [limiteArtes, setLimiteArtes] = useState(0)
  const limiteArtesRef = useRef(0)
  const lastArtAvailabilitySnapshotRef = useRef(null)

  const [pendingSelectId, setPendingSelectId] = useState(null)
  const [pendingCreate, setPendingCreate] = useState(false)

  const selectHandlerRef = useRef(null)
  const createHandlerRef = useRef(null)
  const firstCampaignIdRef = useRef(null)
  const campaignHistoryTotalCountRef = useRef(null)
  const isSyncingFirstCampaignMetaRef = useRef(false)

  const isVisible = Boolean(userData)

  const isChatSocketPage =
    location.pathname.toLowerCase() === '/chatsocket'

  useEffect(() => {
    limiteArtesRef.current = limiteArtes
  }, [limiteArtes])

  useEffect(() => {
    firstCampaignIdRef.current = firstCampaignId
  }, [firstCampaignId])

  useEffect(() => {
    campaignHistoryTotalCountRef.current = campaignHistoryTotalCount
  }, [campaignHistoryTotalCount])

  const refreshArtLimits = useCallback(async () => {
    try {
      const artAvailability = await getArtGenerationAvailability(1, {
        allowCampaignGeneration: true,
      })

      warnIfArtLimitTotalDroppedWithoutUsageIncrease(
        lastArtAvailabilitySnapshotRef.current,
        artAvailability,
        'CampaignSidebarContext',
      )
      lastArtAvailabilitySnapshotRef.current =
        snapshotArtAvailability(artAvailability)

      setAvailableArts(artAvailability?.available ?? null)
      setArtesCriadas(artAvailability?.used ?? 0)
      setLimiteArtes(artAvailability?.total ?? 0)
      return artAvailability
    } catch (error) {
      console.error('Error updating art limits:', error)
      return null
    }
  }, [])

  const syncArtLimits = useCallback((artAvailability) => {
    if (!artAvailability) {
      return
    }

    lastArtAvailabilitySnapshotRef.current =
      snapshotArtAvailability(artAvailability)

    setAvailableArts(artAvailability?.available ?? null)
    setArtesCriadas(artAvailability?.used ?? 0)
    setLimiteArtes(artAvailability?.total ?? 0)
  }, [])

  const syncFirstCampaignMeta = useCallback(async ({
    userAccountId,
    total,
    items,
    searchName = '',
  }) => {
    if (String(searchName || '').trim()) {
      return
    }

    const normalizedTotal = parseKnownNumericValue(total)
    const effectiveTotal =
      normalizedTotal ??
      campaignHistoryTotalCountRef.current ??
      (Array.isArray(items) ? items.length : 0)

    if (effectiveTotal == null) {
      return
    }

    campaignHistoryTotalCountRef.current = effectiveTotal
    setCampaignHistoryTotalCount(effectiveTotal)

    if (effectiveTotal <= 0) {
      firstCampaignIdRef.current = null
      setFirstCampaignId(null)
      return
    }

    if (effectiveTotal <= CAMPAIGNS_PAGE_SIZE) {
      const oldestItem = Array.isArray(items) && items.length > 0
        ? items[items.length - 1]
        : null
      const nextFirstCampaignId = getCampaignHistoryItemId(oldestItem)
      firstCampaignIdRef.current = nextFirstCampaignId || null
      setFirstCampaignId(nextFirstCampaignId || null)
      return
    }

    if (firstCampaignIdRef.current || isSyncingFirstCampaignMetaRef.current) {
      return
    }

    try {
      isSyncingFirstCampaignMetaRef.current = true
      const lastPage = Math.ceil(effectiveTotal / CAMPAIGNS_PAGE_SIZE)
      const response = await getCampaignArtByUser(
        userAccountId,
        lastPage,
        CAMPAIGNS_PAGE_SIZE,
        '',
      )
      const { items: lastPageItems } = parseCampaignPageResponse(response)
      const oldestItem = Array.isArray(lastPageItems) && lastPageItems.length > 0
        ? lastPageItems[lastPageItems.length - 1]
        : null
      const nextFirstCampaignId = getCampaignHistoryItemId(oldestItem)
      firstCampaignIdRef.current = nextFirstCampaignId || null
      setFirstCampaignId(nextFirstCampaignId || null)
    } catch {
      // noop
    } finally {
      isSyncingFirstCampaignMetaRef.current = false
    }
  }, [])

  const fetchCampaignsPage = useCallback(async (page = 1, name = '') => {
    if (!userData) {
      return null
    }

    try {
      setIsLoadingCampaigns(true)
      const userAccount = await UserAccountsService.getUserAccountsByLogin()

      if (!userAccount?.data?.uuid) {
        setIsLoadingCampaigns(false)
        return null
      }

      const response = await getCampaignArtByUser(
        userAccount.data.uuid,
        page,
        CAMPAIGNS_PAGE_SIZE,
        name,
      )

      const { items, total } = parseCampaignPageResponse(response)

      if (page === 1) {
        setCampaigns(items)
      } else {
        setCampaigns((prev) => [...prev, ...items])
      }

      setCampaignsPage(page)

      if (total != null) {
        setHasMoreCampaigns(page * CAMPAIGNS_PAGE_SIZE < total)
      } else {
        setHasMoreCampaigns(items.length === CAMPAIGNS_PAGE_SIZE)
      }

      if (page === 1 && !String(name || '').trim()) {
        await syncFirstCampaignMeta({
          userAccountId: userAccount.data.uuid,
          total,
          items,
          searchName: name,
        })
      }

      setIsLoadingCampaigns(false)
      return items
    } catch {
      setIsLoadingCampaigns(false)
      return null
    }
  }, [syncFirstCampaignMeta, userData])

  const refreshCampaigns = useCallback(async () => {
    return fetchCampaignsPage(1, campaignsSearch)
  }, [campaignsSearch, fetchCampaignsPage])

  const loadMoreCampaigns = useCallback(async () => {
    if (isLoadingCampaigns || !hasMoreCampaigns) {
      return
    }

    await fetchCampaignsPage(campaignsPage + 1, campaignsSearch)
  }, [
    campaignsPage,
    campaignsSearch,
    fetchCampaignsPage,
    hasMoreCampaigns,
    isLoadingCampaigns,
  ])

  const searchCampaigns = useCallback(async (name) => {
    const normalizedName = name || ''
    setCampaignsSearch(normalizedName)
    await fetchCampaignsPage(1, normalizedName)
  }, [fetchCampaignsPage])

  const updateCampaignInList = useCallback((campaignId, patch) => {
    if (!campaignId) {
      return
    }

    setCampaigns((prevData) =>
      prevData.map((campaign) =>
        campaign.id === campaignId ||
        campaign.uuid === campaignId ||
        campaign.campaignArtGenerationId === campaignId
          ? { ...campaign, ...patch }
          : campaign,
      ),
    )
  }, [])

  const registerCampaignHandlers = useCallback((handlers = {}) => {
    selectHandlerRef.current = handlers.onSelect || null
    createHandlerRef.current = handlers.onCreate || null
  }, [])

  const unregisterCampaignHandlers = useCallback(() => {
    selectHandlerRef.current = null
    createHandlerRef.current = null
  }, [])

  const clearPendingActions = useCallback(() => {
    setPendingSelectId(null)
    setPendingCreate(false)
  }, [])

  const navigateToChatSocket = useCallback(() => {
    if (!isChatSocketPage) {
      navigate('/ChatSocket')
    }
  }, [isChatSocketPage, navigate])

  const selectCampaign = useCallback(async (campaignId) => {
    if (!campaignId) {
      return
    }

    setSelectedCampaignId(campaignId)
    persistActiveCampaignForReturn(campaignId)

    if (selectHandlerRef.current) {
      await selectHandlerRef.current(campaignId)
      return
    }

    setPendingSelectId(campaignId)
    navigateToChatSocket()
  }, [navigateToChatSocket])

  const requestCreateCampaign = useCallback(async () => {
    try {
      localStorage.removeItem(LAST_ACTIVE_CAMPAIGN_KEY)
    } catch {
      // noop
    }

    if (createHandlerRef.current) {
      await createHandlerRef.current()
      return
    }

    setPendingCreate(true)
    navigateToChatSocket()
  }, [navigateToChatSocket])

  useEffect(() => {
    if (
      !userData ||
      !hasValidAuthToken() ||
      !isVisible ||
      isPublicAuthRoute(location.pathname)
    ) {
      return
    }

    refreshArtLimits()
    refreshCampaigns()
  }, [isVisible, location.pathname, refreshArtLimits, refreshCampaigns, userData])

  useEffect(() => {
    if (!userData || !isVisible) {
      return
    }

    refreshArtLimits()
  }, [location.pathname, refreshArtLimits, userData, isVisible])

  useEffect(() => {
    if (!userData || !isVisible) {
      return undefined
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshArtLimits()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isVisible, refreshArtLimits, userData])

  useEffect(() => {
    const handlePlanUpdate = (event) => {
      if (
        !shouldSidebarRefreshArtLimitsOnPlanEvent({ isChatSocketPage })
      ) {
        return
      }

      const shouldRetry = Boolean(event?.detail?.retry)

      if (!shouldRetry) {
        refreshArtLimits()
        return
      }

      pollArtCreditsUntilUpdated({
        getBaselineTotal: () => limiteArtesRef.current,
        refresh: refreshArtLimits,
      })
    }

    window.addEventListener('user-plan-count-updated', handlePlanUpdate)
    return () => {
      window.removeEventListener('user-plan-count-updated', handlePlanUpdate)
    }
  }, [isChatSocketPage, refreshArtLimits])

  const value = useMemo(
    () => ({
      isVisible,
      isExpanded,
      setExpanded: setIsExpanded,
      campaigns,
      selectedCampaignId,
      setSelectedCampaignId,
      campaignsSearch,
      isLoadingCampaigns,
      hasMoreCampaigns,
      campaignHistoryTotalCount,
      firstCampaignId,
      firstCampaignIdRef,
      availableArts,
      artesCriadas,
      limiteArtes,
      pendingSelectId,
      pendingCreate,
      refreshCampaigns,
      loadMoreCampaigns,
      searchCampaigns,
      updateCampaignInList,
      refreshArtLimits,
      syncArtLimits,
      selectCampaign,
      requestCreateCampaign,
      registerCampaignHandlers,
      unregisterCampaignHandlers,
      clearPendingActions,
    }),
    [
      artesCriadas,
      availableArts,
      campaignHistoryTotalCount,
      campaigns,
      campaignsSearch,
      clearPendingActions,
      firstCampaignId,
      hasMoreCampaigns,
      isExpanded,
      isLoadingCampaigns,
      isVisible,
      limiteArtes,
      loadMoreCampaigns,
      pendingCreate,
      pendingSelectId,
      refreshArtLimits,
      refreshCampaigns,
      registerCampaignHandlers,
      requestCreateCampaign,
      searchCampaigns,
      selectCampaign,
      selectedCampaignId,
      syncArtLimits,
      unregisterCampaignHandlers,
      updateCampaignInList,
    ],
  )

  return (
    <CampaignSidebarContext.Provider value={value}>
      {children}
    </CampaignSidebarContext.Provider>
  )
}
